import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Checkbox } from './components/ui/checkbox';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { toast, Toaster } from 'sonner';
import { ArrowLeft, Plus, X, User, Heart, Activity, Stethoscope, CheckCircle, Calendar, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

function HomePage() {
  const navigate = useNavigate();

  const mainOptions = [
    {
      title: "Bana Özel Paket",
      description: "Kişiselleştirilmiş sağlık tarama paketi",
      icon: <User className="w-8 h-8" />,
      path: "/personal-package",
      color: "from-emerald-500 to-teal-600"
    },
    {
      title: "Check Up Paketleri",
      description: "Hazır sağlık kontrol paketleri",
      icon: <Heart className="w-8 h-8" />,
      path: "/packages",
      color: "from-blue-500 to-purple-600"
    },
    {
      title: "Manuel",
      description: "Tüm testlerden istediğinizi seçin",
      icon: <Activity className="w-8 h-8" />,
      path: "/manual",
      color: "from-orange-500 to-red-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-16">
          <div className="flex flex-col items-center justify-center mb-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-800 leading-tight">
                MEDIWALI TURKISH
              </h1>
              <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-700 mt-2">
                DIAGNOSTIC CENTER
              </h2>
            </div>
          </div>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Sağlığınız için en uygun tarama paketini seçin ve randevunuzu oluşturun
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {mainOptions.map((option, index) => (
            <Card 
              key={index}
              className="cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl border-0 overflow-hidden group"
              onClick={() => navigate(option.path)}
            >
              <div className={`h-2 bg-gradient-to-r ${option.color}`} />
              <CardHeader className="text-center pb-6">
                <div className={`mx-auto mb-4 p-4 rounded-full bg-gradient-to-r ${option.color} text-white transition-transform group-hover:scale-110`}>
                  {option.icon}
                </div>
                <CardTitle className="text-2xl font-bold text-slate-800 mb-2">
                  {option.title}
                </CardTitle>
                <CardDescription className="text-slate-600 text-lg">
                  {option.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center pb-8">
                <Button 
                  className={`w-full text-white font-semibold py-3 rounded-lg bg-gradient-to-r ${option.color} hover:opacity-90 transition-opacity`}
                  data-testid={`option-${index}-btn`}
                >
                  Başla
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-20 text-center">
          <Card className="max-w-4xl mx-auto bg-white/70 backdrop-blur-sm">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold text-slate-800 mb-6">
                Neden Check-Up Yaptırmalısınız?
              </h2>
              <div className="grid md:grid-cols-3 gap-8 text-left">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Erken Teşhis</h3>
                    <p className="text-slate-600">Sağlık sorunlarını erken dönemde tespit edin</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Kişiselleştirilmiş</h3>
                    <p className="text-slate-600">Yaş, cinsiyet ve yaşam tarzınıza uygun testler</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Güvenilir</h3>
                    <p className="text-slate-600">Uzman doktorlar tarafından değerlendirme</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PersonalPackagePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    family_history: {
      heart_disease: false,
      diabetes: false,
      cancer: false
    },
    lifestyle: {
      smoking: false,
      alcohol_consumption: '',
      active_lifestyle: false,
      obesity_risk: false
    },
    symptoms: []
  });
  const [recommendedTests, setRecommendedTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/personal-package`, formData);
      setRecommendedTests(response.data);
      setSelectedTests(response.data.map(test => test.id));
      setCurrentStep(2);
    } catch (error) {
      toast.error('Paket önerileri alınırken hata oluştu');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeTest = (testId) => {
    setSelectedTests(prev => prev.filter(id => id !== testId));
  };

  const handleContinue = () => {
    if (selectedTests.length === 0) {
      toast.error('Lütfen en az bir test seçin');
      return;
    }
    navigate('/personal-info', { 
      state: { 
        selectedTests: selectedTests,
        packageType: 'custom'
      }
    });
  };

  if (currentStep === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="mr-4"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ana Sayfa
            </Button>
            <h1 className="text-3xl font-bold text-slate-800">Mediwali - Bana Özel Paket</h1>
          </div>

          <Card className="max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl">Kişisel Bilgileriniz</CardTitle>
              <CardDescription>
                Size uygun testleri belirlemek için aşağıdaki soruları cevaplayın
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Age and Gender */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="age">Yaşınız</Label>
                  <Input
                    id="age"
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: parseInt(e.target.value) || ''})}
                    placeholder="Örn: 35"
                    className="mt-1"
                    data-testid="age-input"
                  />
                </div>
                <div>
                  <Label>Cinsiyetiniz</Label>
                  <RadioGroup 
                    value={formData.gender} 
                    onValueChange={(value) => setFormData({...formData, gender: value})}
                    className="mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="male" id="male" data-testid="gender-male" />
                      <Label htmlFor="male">Erkek</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="female" id="female" data-testid="gender-female" />
                      <Label htmlFor="female">Kadın</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* Family History */}
              <div>
                <Label className="text-lg font-semibold">Aile Geçmişi</Label>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="heart-disease"
                      checked={formData.family_history.heart_disease}
                      onCheckedChange={(checked) => 
                        setFormData({
                          ...formData, 
                          family_history: {...formData.family_history, heart_disease: checked}
                        })
                      }
                      data-testid="family-heart-disease"
                    />
                    <Label htmlFor="heart-disease">Ailede erken yaşta kalp hastalığı öyküsü var</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="diabetes"
                      checked={formData.family_history.diabetes}
                      onCheckedChange={(checked) => 
                        setFormData({
                          ...formData, 
                          family_history: {...formData.family_history, diabetes: checked}
                        })
                      }
                      data-testid="family-diabetes"
                    />
                    <Label htmlFor="diabetes">Ailede diyabet öyküsü var</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="cancer"
                      checked={formData.family_history.cancer}
                      onCheckedChange={(checked) => 
                        setFormData({
                          ...formData, 
                          family_history: {...formData.family_history, cancer: checked}
                        })
                      }
                      data-testid="family-cancer"
                    />
                    <Label htmlFor="cancer">Ailede kanser öyküsü var</Label>
                  </div>
                </div>
              </div>

              {/* Lifestyle */}
              <div>
                <Label className="text-lg font-semibold">Yaşam Tarzı</Label>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="smoking"
                      checked={formData.lifestyle.smoking}
                      onCheckedChange={(checked) => 
                        setFormData({
                          ...formData, 
                          lifestyle: {...formData.lifestyle, smoking: checked}
                        })
                      }
                      data-testid="lifestyle-smoking"
                    />
                    <Label htmlFor="smoking">Sigara kullanıyorum</Label>
                  </div>
                  <div>
                    <Label htmlFor="alcohol">Alkol tüketimi</Label>
                    <Select 
                      value={formData.lifestyle.alcohol_consumption} 
                      onValueChange={(value) => 
                        setFormData({
                          ...formData, 
                          lifestyle: {...formData.lifestyle, alcohol_consumption: value}
                        })
                      }
                    >
                      <SelectTrigger className="mt-1" data-testid="alcohol-select">
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kullanmıyorum</SelectItem>
                        <SelectItem value="low">Az (Sosyal)</SelectItem>
                        <SelectItem value="moderate">Orta</SelectItem>
                        <SelectItem value="high">Fazla</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="active-lifestyle"
                      checked={formData.lifestyle.active_lifestyle}
                      onCheckedChange={(checked) => 
                        setFormData({
                          ...formData, 
                          lifestyle: {...formData.lifestyle, active_lifestyle: checked}
                        })
                      }
                      data-testid="lifestyle-active"
                    />
                    <Label htmlFor="active-lifestyle">Düzenli spor yapıyorum / Aktif yaşam tarzım var</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="obesity-risk"
                      checked={formData.lifestyle.obesity_risk}
                      onCheckedChange={(checked) => 
                        setFormData({
                          ...formData, 
                          lifestyle: {...formData.lifestyle, obesity_risk: checked}
                        })
                      }
                      data-testid="lifestyle-obesity"
                    />
                    <Label htmlFor="obesity-risk">Obezite / Kilo kontrolü sorunu yaşıyorum</Label>
                  </div>
                </div>
              </div>

              {/* Symptoms */}
              <div>
                <Label htmlFor="symptoms" className="text-lg font-semibold">Şikayetleriniz (Varsa)</Label>
                <Textarea
                  id="symptoms"
                  placeholder="Şikayetlerinizi seçin: yorgunluk, baş ağrısı, göğüs ağrısı, sindirim sorunları, idrar şikayetleri vb."
                  value={formData.symptoms.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData, 
                    symptoms: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  className="mt-2"
                  rows={3}
                  data-testid="symptoms-textarea"
                />
              </div>

              <Button 
                onClick={handleFormSubmit} 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-lg"
                disabled={!formData.age || !formData.gender || loading}
                data-testid="generate-package-btn"
              >
                {loading ? 'Paket Hazırlanıyor...' : 'Bana Özel Paketi Oluştur'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step 2: Show recommended tests with remove buttons
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => setCurrentStep(1)}
            className="mr-4"
            data-testid="back-to-form-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">Kişiselleştirilmiş Check-Up Paketiniz</h1>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-emerald-600">
                Size Önerilen Testler
              </CardTitle>
              <CardDescription>
                Verdiğiniz bilgilere göre size özel hazırlanmış testler. İstemediğiniz testleri çıkarabilirsiniz.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recommendedTests
                  .filter(test => selectedTests.includes(test.id))
                  .map((test, index) => (
                  <div 
                    key={test.id}
                    className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center justify-center w-8 h-8 bg-emerald-100 text-emerald-600 font-semibold text-sm rounded-full">
                          {index + 1}
                        </span>
                        <div>
                          <h3 className="font-medium text-slate-800">{test.name}</h3>
                          {test.description && (
                            <p className="text-sm text-slate-600 mt-1">{test.description}</p>
                          )}
                          <span className="inline-block bg-emerald-100 text-emerald-700 text-xs px-2 py-1 rounded-full mt-2">
                            {test.category}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeTest(test.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                      data-testid={`remove-test-${test.id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Sil
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Toplam Test Sayısı: {selectedTests.length}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Paketinizi onaylamak için devam edin
                </p>
              </div>
              <Button 
                onClick={handleContinue}
                disabled={selectedTests.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-lg"
                data-testid="continue-to-personal-info-btn"
              >
                Paketi Onayla ve Devam Et
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManualPage() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const [testsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/tests`),
        axios.get(`${API_BASE_URL}/api/tests/categories`)
      ]);
      setTests(testsResponse.data);
      setCategories(['all', ...categoriesResponse.data.categories]);
    } catch (error) {
      toast.error('Testler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredTests = selectedCategory === 'all' 
    ? tests 
    : tests.filter(test => test.category === selectedCategory);

  const toggleTest = (testId) => {
    setSelectedTests(prev => {
      const newSelection = prev.includes(testId) 
        ? prev.filter(id => id !== testId)
        : [...prev, testId];
      console.log('Toggle test:', testId, 'New count:', newSelection.length);
      return newSelection;
    });
  };

  const handleContinue = () => {
    if (selectedTests.length === 0) {
      toast.error('Lütfen en az bir test seçin');
      return;
    }
    navigate('/personal-info', { 
      state: { 
        selectedTests: selectedTests,
        packageType: 'manual'
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-orange-600 mx-auto mb-4 animate-spin" />
          <p className="text-lg text-slate-600">Testler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mr-4"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ana Sayfa
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">Mediwali - Manuel Test Seçimi</h1>
        </div>

        <div className="mb-6 relative z-50">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-64" data-testid="category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-50">
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.filter(cat => cat !== 'all').map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TestSelectionView 
          tests={filteredTests}
          selectedTests={selectedTests}
          onToggleTest={toggleTest}
          onContinue={handleContinue}
        />
      </div>
    </div>
  );
}

function TestSelectionView({ tests, selectedTests, onToggleTest, onContinue }) {
  const groupedTests = tests.reduce((groups, test) => {
    const category = test.category;
    if (!groups[category]) groups[category] = [];
    groups[category].push(test);
    return groups;
  }, {});

  return (
    <div className="space-y-8">
      {Object.entries(groupedTests).map(([category, categoryTests]) => (
        <Card key={category} className="overflow-hidden">
          <CardHeader className="bg-slate-50">
            <CardTitle className="text-xl text-slate-800">
              {category}
              <Badge className="ml-3" variant="secondary">
                {categoryTests.filter(test => selectedTests.includes(test.id)).length} / {categoryTests.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid gap-2 p-6">
              {categoryTests.map(test => (
                <div 
                  key={test.id} 
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    selectedTests.includes(test.id) 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-slate-800">{test.name}</h3>
                    {test.description && (
                      <p className="text-sm text-slate-600 mt-1">{test.description}</p>
                    )}
                  </div>
                  <Button
                    variant={selectedTests.includes(test.id) ? "destructive" : "default"}
                    size="sm"
                    onClick={() => onToggleTest(test.id)}
                    className={selectedTests.includes(test.id) 
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                    }
                    data-testid={`test-toggle-${test.id}`}
                  >
                    {selectedTests.includes(test.id) ? (
                      <><X className="w-4 h-4 mr-1" /> Sil</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-1" /> Seç</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="sticky bottom-0 bg-white border-t p-6 shadow-lg">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="text-lg font-semibold text-slate-800">
            Seçilen Test Sayısı: <span className="text-orange-600">{selectedTests.length}</span>
          </div>
          <Button 
            onClick={onContinue}
            disabled={selectedTests.length === 0}
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 text-lg"
            data-testid="continue-to-personal-info-btn"
          >
            Paketi Oluştur
          </Button>
        </div>
      </div>
    </div>
  );
}

function PackageCategoryPage() {
  const navigate = useNavigate();
  const { categoryName } = useParams();
  const decodedCategoryName = decodeURIComponent(categoryName);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategoryPackages();
  }, [categoryName]);

  const fetchCategoryPackages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/packages`);
      const categoryPackages = response.data.filter(pkg => pkg.category === decodedCategoryName);
      setPackages(categoryPackages);
    } catch (error) {
      toast.error('Paketler yüklenirken hata oluştu');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'Standart': 'from-emerald-500 to-teal-600',
      'Kontrol': 'from-emerald-500 to-teal-600',
      'Derin Tarama': 'from-blue-500 to-purple-600',
      'Detaylı': 'from-blue-500 to-purple-600',
      'Plus': 'from-blue-500 to-purple-600',
      'Premium': 'from-purple-500 to-pink-600',
      'Exclusive': 'from-purple-500 to-pink-600'
    };
    return colors[level] || 'from-gray-500 to-gray-600';
  };

  const handleSelectPackage = (packageId, packageData) => {
    navigate('/package-tests', {
      state: {
        packageId,
        packageName: packageData.name,
        packageTests: packageData.tests,
        packageType: 'preset'
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-lg text-slate-600">Paketler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/packages')}
            className="mr-4"
            data-testid="back-to-packages-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Paketler
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">{decodedCategoryName}</h1>
        </div>

        <div className="text-center mb-12">
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {decodedCategoryName} için hazırlanmış farklı kapsamdaki paketlerden size uygun olanını seçin.
          </p>
        </div>

        <div className="grid md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {packages.map((pkg, index) => (
            <Card 
              key={pkg.id}
              className="transform transition-all duration-300 hover:scale-105 hover:shadow-xl border-0 overflow-hidden"
            >
              <div className={`h-3 bg-gradient-to-r ${getLevelColor(pkg.level)}`} />
              <CardHeader className="text-center pb-6">
                <div className={`mx-auto mb-4 p-4 rounded-full bg-gradient-to-r ${getLevelColor(pkg.level)} text-white`}>
                  <Stethoscope className="w-8 h-8" />
                </div>
                <CardTitle className="text-xl font-bold text-slate-800 mb-2">
                  {pkg.level}
                </CardTitle>
                <CardTitle className="text-lg font-semibold text-slate-700 mb-3">
                  {pkg.name}
                </CardTitle>
                <CardDescription className="text-slate-600">
                  {pkg.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center pb-8">
                <div className="flex items-center justify-center text-sm text-slate-500 mb-6">
                  <Activity className="w-4 h-4 mr-2" />
                  {pkg.tests.length} test içerir
                </div>
                <Button 
                  onClick={() => handleSelectPackage(pkg.id, pkg)}
                  className={`w-full text-white font-semibold py-3 rounded-lg bg-gradient-to-r ${getLevelColor(pkg.level)} hover:opacity-90 transition-opacity`}
                  data-testid={`select-package-${pkg.id}`}
                >
                  Paketi Seç
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {packages.length === 0 && !loading && (
          <div className="text-center py-12">
            <Activity className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Bu kategoride henüz paket bulunmuyor</h2>
            <p className="text-slate-500 mb-6">Yakında yeni paketler eklenecek</p>
            <Button 
              onClick={() => navigate('/packages')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Diğer Paketlere Dön
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PackageTestsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { packageId, packageName, packageTests, packageType } = location.state || {};
  
  const [tests, setTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (packageTests) {
      fetchTestDetails();
    } else {
      navigate('/packages');
    }
  }, [packageTests]);

  const fetchTestDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tests`);
      const allTests = response.data;
      const packageTestDetails = allTests.filter(test => packageTests.includes(test.id));
      setTests(packageTestDetails);
      setSelectedTests(packageTests);
    } catch (error) {
      toast.error('Test detayları yüklenirken hata oluştu');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTest = (testId) => {
    setSelectedTests(prev => {
      const newSelection = prev.includes(testId) 
        ? prev.filter(id => id !== testId)
        : [...prev, testId];
      console.log('Toggle test:', testId, 'New count:', newSelection.length);
      return newSelection;
    });
  };

  const handleContinue = () => {
    if (selectedTests.length === 0) {
      toast.error('Lütfen en az bir test seçin');
      return;
    }
    navigate('/personal-info', { 
      state: { 
        selectedTests: selectedTests,
        packageType: packageType
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-lg text-slate-600">Test detayları yükleniyor...</p>
        </div>
      </div>
    );
  }

  const groupedTests = tests.reduce((groups, test) => {
    const category = test.category;
    if (!groups[category]) groups[category] = [];
    groups[category].push(test);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mr-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">{packageName}</h1>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-blue-600">
                Paket İçeriği
              </CardTitle>
              <CardDescription>
                Bu pakette yer alan testleri görüntüleyip istediğinizi çıkarabilirsiniz.
              </CardDescription>
            </CardHeader>
          </Card>

          {Object.entries(groupedTests).map(([category, categoryTests]) => (
            <Card key={category} className="overflow-hidden">
              <CardHeader className="bg-slate-50">
                <CardTitle className="text-xl text-slate-800">
                  {category}
                  <Badge className="ml-3" variant="secondary">
                    {categoryTests.filter(test => selectedTests.includes(test.id)).length} / {categoryTests.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid gap-2 p-6">
                  {categoryTests.map((test, index) => (
                    <div 
                      key={test.id} 
                      className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                        selectedTests.includes(test.id) 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 font-semibold text-sm rounded-full">
                            {index + 1}
                          </span>
                          <div>
                            <h3 className="font-medium text-slate-800">{test.name}</h3>
                            {test.description && (
                              <p className="text-sm text-slate-600 mt-1">{test.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant={selectedTests.includes(test.id) ? "destructive" : "default"}
                        size="sm"
                        onClick={() => toggleTest(test.id)}
                        className={selectedTests.includes(test.id) 
                          ? 'bg-red-500 hover:bg-red-600'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }
                        data-testid={`test-toggle-${test.id}`}
                      >
                        {selectedTests.includes(test.id) ? (
                          <><X className="w-4 h-4 mr-1" /> Sil</>
                        ) : (
                          <><Plus className="w-4 h-4 mr-1" /> Seç</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Toplam Test Sayısı: {selectedTests.length}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Paketinizi onaylamak için devam edin
                </p>
              </div>
              <Button 
                onClick={handleContinue}
                disabled={selectedTests.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
                data-testid="continue-to-personal-info-btn"
              >
                Paketi Onayla ve Devam Et
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PackagesPage() {
  const navigate = useNavigate();
  const [packageCategories, setPackageCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackageCategories();
  }, []);

  const fetchPackageCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/packages/categories`);
      setPackageCategories(response.data.categories);
    } catch (error) {
      toast.error('Paket kategorileri yüklenirken hata oluştu');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (index) => {
    const colors = [
      "from-emerald-500 to-teal-600",
      "from-red-500 to-pink-600", 
      "from-purple-500 to-indigo-600",
      "from-orange-500 to-red-600",
      "from-blue-500 to-purple-600",
      "from-green-500 to-blue-600",
      "from-cyan-500 to-blue-600",
      "from-pink-500 to-rose-600",
      "from-blue-600 to-indigo-600",
      "from-yellow-500 to-orange-600",
      "from-indigo-500 to-purple-600",
      "from-teal-500 to-green-600",
      "from-violet-500 to-purple-600"
    ];
    return colors[index % colors.length];
  };

  const getCategoryDescription = (categoryName) => {
    const descriptions = {
      "Genel Sağlık Paketi": "Temel sağlık tarama paketleri",
      "Kardiyoloji Paketi": "Kalp sağlığı tarama paketleri", 
      "Endokrin & Metabolizma Paketi": "Hormon ve metabolizma taraması",
      "Onkoloji & Kanser Taramaları": "Kanser tarama paketleri",
      "Nörolojik Değerlendirme Paketi": "Sinir sistemi taraması",
      "Gastroenteroloji Paketi": "Sindirim sistemi taraması", 
      "Üroloji & Nefroloji Paketi": "İdrar yolu ve böbrek taraması",
      "Kadın Sağlığı Paketi": "Kadın sağlığı taraması",
      "Erkek Sağlığı Paketi": "Erkek sağlığı taraması",
      "Ortopedi & Kemik Sağlığı Paketi": "Kemik ve eklem sağlığı",
      "Enfeksiyon & İmmünoloji Paketi": "Bağışıklık sistemi taraması",
      "Göz–Kulak–Diş Paketi": "Göz, kulak, diş sağlığı",
      "Yaşam Tarzı & Performans Paketi": "Performans ve yaşam kalitesi"
    };
    return descriptions[categoryName] || "Özel sağlık tarama paketi";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-lg text-slate-600">Paketler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mr-4"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ana Sayfa
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">Mediwali - Check-Up Paketleri</h1>
        </div>

        <div className="text-center mb-12">
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            İhtiyacınıza uygun sağlık tarama paketlerini seçin. Her kategori içinde 3 farklı kapsamda paket bulunmaktadır.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packageCategories.map((category, index) => (
            <Card 
              key={index}
              className="cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-lg border-0 overflow-hidden group"
              onClick={() => navigate(`/package-category/${encodeURIComponent(category)}`)}
            >
              <div className={`h-2 bg-gradient-to-r ${getCategoryColor(index)}`} />
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold text-slate-800 group-hover:text-slate-900 transition-colors">
                  {category}
                </CardTitle>
                <CardDescription className="text-slate-600">
                  {getCategoryDescription(category)}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0 pb-6">
                <div className="flex items-center text-sm text-slate-500 mb-4">
                  <Calendar className="w-4 h-4 mr-2" />
                  3 farklı paket seçeneği
                </div>
                <Button 
                  className={`w-full text-white font-medium bg-gradient-to-r ${getCategoryColor(index)} hover:opacity-90 transition-opacity`}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}-btn`}
                >
                  Paketleri Görüntüle
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Card className="max-w-3xl mx-auto bg-white/70 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">
                Hangi Paketi Seçmeliyim?
              </h2>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2 text-emerald-600">Standart Paket</h3>
                  <p className="text-sm text-slate-600">Temel tarama testleri ve hızlı sonuçlar</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2 text-blue-600">Derin Tarama</h3>
                  <p className="text-sm text-slate-600">Kapsamlı analiz ve ek görüntülemeler</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2 text-purple-600">Premium</h3>
                  <p className="text-sm text-slate-600">En detaylı inceleme ve uzman konsültasyonları</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PersonalInfoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedTests, packageType } = location.state || {};
  
  const [personalInfo, setPersonalInfo] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!personalInfo.first_name || !personalInfo.last_name || !personalInfo.phone) {
      toast.error('Ad, soyad ve telefon numarası gereklidir');
      return;
    }

    try {
      setLoading(true);
      const orderData = {
        package_type: packageType,
        selected_tests: selectedTests,
        personal_info: personalInfo,
        status: 'pending'
      };

      const response = await axios.post(`${API_BASE_URL}/api/orders`, orderData);
      navigate('/confirmation', { state: { orderId: response.data.id } });
    } catch (error) {
      toast.error('Sipariş oluşturulurken hata oluştu');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedTests) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mr-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">Mediwali - Kişisel Bilgiler</h1>
        </div>

        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center">
              <User className="w-6 h-6 mr-2 text-emerald-600" />
              İletişim Bilgileri
            </CardTitle>
            <CardDescription>
              Randevu onayı için iletişim bilgilerinizi girin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">Ad *</Label>
                  <Input
                    id="firstName"
                    value={personalInfo.first_name}
                    onChange={(e) => setPersonalInfo({...personalInfo, first_name: e.target.value})}
                    placeholder="Adınız"
                    required
                    className="mt-1"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Soyad *</Label>
                  <Input
                    id="lastName"
                    value={personalInfo.last_name}
                    onChange={(e) => setPersonalInfo({...personalInfo, last_name: e.target.value})}
                    placeholder="Soyadınız"
                    required
                    className="mt-1"
                    data-testid="last-name-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone">Telefon Numarası *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    id="phone"
                    type="tel"
                    value={personalInfo.phone}
                    onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
                    placeholder="0555 123 45 67"
                    required
                    className="pl-10"
                    data-testid="phone-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">E-posta (Opsiyonel)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    id="email"
                    type="email"
                    value={personalInfo.email}
                    onChange={(e) => setPersonalInfo({...personalInfo, email: e.target.value})}
                    placeholder="ornek@email.com"
                    className="pl-10"
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Seçilen Test Sayısı:</strong> {selectedTests.length}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Paket Türü:</strong> {packageType === 'custom' ? 'Kişiselleştirilmiş' : 'Manuel Seçim'}
                </p>
              </div>

              <Button 
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-lg"
                disabled={loading}
                data-testid="confirm-order-btn"
              >
                {loading ? 'Onaylanıyor...' : 'Paketi Onayla'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId } = location.state || {};

  if (!orderId) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <Card className="max-w-md mx-auto text-center">
        <CardContent className="p-12">
          <div className="mb-6">
            <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Başarılı!</h1>
            <p className="text-slate-600">
              Check-up paketiniz başarıyla oluşturuldu.
            </p>
          </div>
          
          <div className="bg-slate-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-slate-600 mb-2">
              <strong>Sipariş No:</strong>
            </p>
            <p className="font-mono text-lg text-slate-800 break-all">{orderId}</p>
          </div>
          
          <p className="text-sm text-slate-600 mb-6">
            Kısa süre içinde sizinle iletişime geçeceğiz.
          </p>
          
          <Button 
            onClick={() => navigate('/')}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="back-to-home-btn"
          >
            Ana Sayfaya Dön
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/personal-package" element={<PersonalPackagePage />} />
          <Route path="/manual" element={<ManualPage />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/package-category/:categoryName" element={<PackageCategoryPage />} />
          <Route path="/package-tests" element={<PackageTestsPage />} />
          <Route path="/personal-info" element={<PersonalInfoPage />} />
          <Route path="/confirmation" element={<ConfirmationPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
